import { useEffect, useMemo, useState } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import { Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../../api";
import ReportingTreeManager from "../Users/ReportingTreeManager";
import "./AdminTaskKanban.css";

const getId = (item) => item?._id || item?.id || "";

const getEmployeeIdFromTask = (task) => {
  if (task?.employeeUserId) return String(task.employeeUserId);
  if (task?.employeeId) return String(task.employeeId);
  if (task?.assignedEmployeeId) return String(task.assignedEmployeeId);
  if (task?.assignedEmployee?._id) return String(task.assignedEmployee._id);
  if (task?.assignedTo?._id) return String(task.assignedTo._id);
  if (typeof task?.assignedEmployee === "string") return task.assignedEmployee;
  return "unassigned";
};

const getEmployeeUserIdFromTask = (task) => {
  if (task?.assignedEmployee?.userId?._id) return String(task.assignedEmployee.userId._id);
  if (task?.assignedEmployee?.user?._id) return String(task.assignedEmployee.user._id);
  if (task?.employeeId?.userId?._id) return String(task.employeeId.userId._id);
  if (task?.employeeId?.user?._id) return String(task.employeeId.user._id);
  if (task?.assignedEmployee?.userId) return String(task.assignedEmployee.userId);
  if (task?.assignedEmployee?.user) return String(task.assignedEmployee.user);
  if (task?.employeeId?.userId) return String(task.employeeId.userId);
  if (task?.employeeId?.user) return String(task.employeeId.user);
  return "";
};

const getWorkOrderIdFromTask = (task) => {
  if (task?.workOrder?._id) return String(task.workOrder._id);
  if (typeof task?.workOrder === "string") return task.workOrder;
  if (task?.workOrderId) return String(task.workOrderId);
  return "";
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatInputDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().split("T")[0];
};

const normalize = (value) => String(value || "").toLowerCase().trim();

const getLiveTaskStatus = (task) => {
  const status = String(task?.status || "");
  if (normalize(status) === "completed") return "Completed";
  if (normalize(status) === "delayed") return "Delayed";

  const deadline = task?.plannedEndDate || task?.dueDate ? new Date(task.plannedEndDate || task.dueDate) : null;
  if (deadline && !Number.isNaN(deadline.getTime())) {
    const delayStartDate = new Date(deadline);
    delayStartDate.setDate(delayStartDate.getDate() + 1);
    delayStartDate.setHours(0, 0, 0, 0);

    if (new Date() >= delayStartDate) {
      return "Delayed";
    }
  }

  return status || "Unassigned";
};

const isTaskOverdue = (task) => {
  if (normalize(task?.status) === "completed") return false;
  if (getLiveTaskStatus(task) === "Delayed") return true;
  const deadline = task?.plannedEndDate || task?.dueDate ? new Date(task.plannedEndDate || task.dueDate) : null;
  if (!deadline || Number.isNaN(deadline.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return deadline < today;
};

const getNextDueInfo = (taskList) => {
  const pending = taskList.filter((t) => normalize(t.status) !== "completed");
  if (!pending.length) return { dateStr: "—", statusText: "", statusType: "none" };

  const validDated = pending
    .map((t) => {
      const d = t.plannedEndDate || t.dueDate;
      const parsed = d ? new Date(d) : null;
      return { task: t, date: parsed, time: parsed && !Number.isNaN(parsed.getTime()) ? parsed.getTime() : Infinity };
    })
    .sort((a, b) => a.time - b.time);

  const earliest = validDated[0];
  if (!earliest || earliest.time === Infinity) {
    return { dateStr: "No due date", statusText: "", statusType: "none" };
  }

  const dueDate = earliest.date;
  const now = new Date();
  const dateStr = formatDate(dueDate);

  const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dueMidnight = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate()).getTime();
  const diffDays = Math.round((dueMidnight - todayMidnight) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    const overdueDays = Math.abs(diffDays);
    const weeks = Math.floor(overdueDays / 7);
    const statusText = weeks >= 1 ? `${weeks} wk${weeks > 1 ? "s" : ""} overdue` : "Overdue";
    return { dateStr, statusText, statusType: "overdue" };
  } else if (diffDays <= 3) {
    return { dateStr, statusText: "Due soon", statusType: "due-soon" };
  } else {
    return { dateStr, statusText: "", statusType: "normal" };
  }
};

const statusClass = (status) => {
  const value = normalize(status);
  if (value.includes("complete")) return "status-completed";
  if (value.includes("progress")) return "status-progress";
  if (value.includes("delay")) return "status-delayed";
  return "status-default";
};

const priorityClass = (priority) => {
  const value = normalize(priority);
  if (value === "critical") return "priority-critical";
  if (value === "high") return "priority-high";
  if (value === "low") return "priority-low";
  return "priority-medium";
};

const getInitials = (name) =>
  String(name || "NA")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

const getUserIdCandidates = (user) =>
  [
    user?._id,
    user?.id,
    user?.userId,
    user?.employeeId,
    user?.employee?._id,
    user?.employee,
  ]
    .filter(Boolean)
    .map(String);

const isSameId = (left, right) => left && right && String(left) === String(right);

const getRoleName = (user) => {
  if (typeof user?.role === "string") return normalize(user.role);
  if (Array.isArray(user?.role)) return normalize(user.role[0]?.name);
  return normalize(user?.role?.name || user?.roleName);
};

const getEmployeeUserId = (employee) => {
  if (employee?.userId?._id) return String(employee.userId._id);
  if (employee?.user?._id) return String(employee.user._id);
  if (employee?.userId) return String(employee.userId);
  if (employee?.user) return String(employee.user);
  return "";
};

const getStaffStatusColumn = (status) => {
  const value = normalize(status);
  if (value.includes("complete")) return "Completed";
  if (value.includes("progress")) return "In Progress";
  if (value.includes("delay")) return "Delayed";
  if (value.includes("assign")) return "Assigned";
  return "Unassigned";
};

const staffColumnOrder = ["Assigned", "In Progress", "Completed", "Delayed"];

const staffColumnStatusPayload = {
  Assigned: "Assigned",
  "In Progress": "In Progress",
  Completed: "Completed",
  Delayed: "Delayed",
  Unassigned: "Unassigned",
};

const AdminTaskKanban = ({ viewMode = "admin" }) => {
  const isStaffView = viewMode === "staff";
  const currentUser = useSelector((state) => state.auth?.userDetails || {});
  const navigate = useNavigate();
  const canManageReportingTree = getRoleName(currentUser) === "admin";
  const [searchParams] = useSearchParams();
  const workOrderFromUrl = searchParams.get("workOrder") || "";

  const [employees, setEmployees] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingTask, setSavingTask] = useState(false);
  const [draggedTaskId, setDraggedTaskId] = useState(null);
  const [activeColumnId, setActiveColumnId] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [editTaskModalOpen, setEditTaskModalOpen] = useState(false);
  const [editTaskId, setEditTaskId] = useState("");
  const [deleteTaskCandidate, setDeleteTaskCandidate] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [savingComment, setSavingComment] = useState(false);

  // Active view: 'pending' (Pending Overview) | 'kanban' (Kanban Board) | 'reporting' (Reporting Tree)
  const [boardView, setBoardView] = useState(isStaffView ? "kanban" : "pending");
  const [sortBy, setSortBy] = useState("overdue");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [expandedEmployeeIds, setExpandedEmployeeIds] = useState([]);

  const [taskForm, setTaskForm] = useState({
    workOrder: workOrderFromUrl,
    assignedEmployeeId: "",
    title: "",
    description: "",
    priority: "Medium",
    estimatedHours: 2,
    plannedEndDate: "",
  });

  const [editTaskForm, setEditTaskForm] = useState({
    workOrder: "",
    assignedEmployeeId: "",
    title: "",
    description: "",
    priority: "Medium",
    status: "Unassigned",
    estimatedHours: 2,
    plannedEndDate: "",
  });

  const [filters, setFilters] = usePersistentFilters("admin_task_kanban_filters", {
    search: "",
    status: "",
    priority: "",
    employee: "",
    workOrder: workOrderFromUrl,
  });

  useEffect(() => {
    fetchBoardData();
  }, []);

  useEffect(() => {
    setFilters((current) => ({ ...current, workOrder: workOrderFromUrl }));
    setTaskForm((current) => ({ ...current, workOrder: workOrderFromUrl }));
  }, [workOrderFromUrl]);

  const fetchAssignableUsers = async (workOrderId = "") => {
    const query = workOrderId ? `?workOrder=${encodeURIComponent(workOrderId)}` : "";
    const response = await api.get(`/users/assignable-task-users${query}`);
    const employeeData = response.data?.data || response.data || [];

    return (Array.isArray(employeeData) ? employeeData : []).filter((emp) => {
      const role = normalize(emp.role?.name || emp.roleName || emp.role || "");
      return role !== "client";
    });
  };

  const fetchBoardData = async () => {
    setLoading(true);
    try {
      if (isStaffView) {
        const tasksRes = await api.get("/tasks?limit=500");
        const taskData =
          tasksRes.data?.data ||
          tasksRes.data?.tasks ||
          tasksRes.data ||
          [];

        setTasks(Array.isArray(taskData) ? taskData : []);
        setEmployees([]);
        setWorkOrders([]);
        return;
      }

      const [employeesRes, tasksRes, workOrdersRes] = await Promise.allSettled([
        fetchAssignableUsers(workOrderFromUrl || filters.workOrder || taskForm.workOrder),
        api.get("/tasks?limit=500"),
        api.get("/work-orders"),
      ]);

      const taskData =
        tasksRes.value?.data?.data ||
        tasksRes.value?.data?.tasks ||
        tasksRes.value?.data ||
        [];

      const workOrderData =
        workOrdersRes.value?.data?.data ||
        workOrdersRes.value?.data?.workOrders ||
        workOrdersRes.value?.data ||
        [];

      setEmployees(Array.isArray(employeesRes.value) ? employeesRes.value : []);
      setTasks(Array.isArray(taskData) ? taskData : []);
      setWorkOrders(Array.isArray(workOrderData) ? workOrderData : []);

      if (tasksRes.status === "rejected") {
        toast.error(tasksRes.reason?.response?.data?.message || "Failed to load tasks");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load tasks board");
    } finally {
      setLoading(false);
    }
  };

  // KPI Calculations
  const kpiData = useMemo(() => {
    const overdueTasksCount = tasks.filter(isTaskOverdue).length;
    const highPriorityCount = tasks.filter(
      (t) =>
        normalize(t.status) !== "completed" &&
        ["high", "critical"].includes(normalize(t.priority))
    ).length;
    const totalPendingCount = tasks.filter((t) => normalize(t.status) !== "completed").length;

    const overdueEmployeeIds = new Set();
    tasks.forEach((t) => {
      if (isTaskOverdue(t)) {
        const empId = getEmployeeIdFromTask(t);
        if (empId) overdueEmployeeIds.add(empId);
      }
    });

    return {
      overdueTasksCount,
      highPriorityCount,
      totalPendingCount,
      employeesWithOverdueCount: overdueEmployeeIds.size,
    };
  }, [tasks]);

  // Board columns for Kanban view
  const boardColumns = useMemo(() => {
    const search = normalize(filters.search);

    if (isStaffView) {
      const userIds = getUserIdCandidates(currentUser);
      const matchedEmployee = employees.find((employee) => {
        const employeeId = getId(employee);
        const employeeUserId = getEmployeeUserId(employee);
        const emailMatch =
          currentUser?.email &&
          employee?.email &&
          normalize(currentUser.email) === normalize(employee.email);

        return (
          userIds.some((id) => isSameId(id, employeeId)) ||
          userIds.some((id) => isSameId(id, employeeUserId)) ||
          emailMatch
        );
      });
      const matchedEmployeeId = getId(matchedEmployee);

      const myTasks = tasks.filter((task) => {
        const taskEmployeeId = getEmployeeIdFromTask(task);
        const taskEmployeeUserId = getEmployeeUserIdFromTask(task);
        const assignedToMe =
          userIds.some((id) => isSameId(id, taskEmployeeId)) ||
          userIds.some((id) => isSameId(id, taskEmployeeUserId)) ||
          isSameId(matchedEmployeeId, taskEmployeeId) ||
          (currentUser?.name &&
            task.employeeName &&
            normalize(currentUser.name) === normalize(task.employeeName));

        if (!assignedToMe) return false;

        const titleMatch = normalize(task.title).includes(search);
        const descMatch = normalize(task.description).includes(search);
        const woMatch = normalize(task.workOrder?.woNo).includes(search);
        const priorityMatch = !filters.priority || task.priority === filters.priority;
        const workOrderMatch =
          !filters.workOrder || getWorkOrderIdFromTask(task) === filters.workOrder;

        return (!search || titleMatch || descMatch || woMatch) && priorityMatch && workOrderMatch;
      });

      return staffColumnOrder
        .map((statusName) => ({
          column: {
            _id: statusName,
            name: statusName,
            role: "Task status",
          },
          tasks: myTasks.filter((task) => getStaffStatusColumn(getLiveTaskStatus(task)) === statusName),
        }))
        .filter((column) => !filters.status || column.column.name === filters.status);
    }

    const baseColumns = [
      {
        _id: "unassigned",
        name: "Unassigned",
        role: "No owner",
        department: "Backlog",
      },
      ...employees,
    ];

    return baseColumns
      .filter((employee) => {
        if (!filters.employee) return true;
        return getId(employee) === filters.employee;
      })
      .map((employee) => {
        const employeeId = getId(employee);
        const employeeTasks = tasks
          .filter((task) => getEmployeeIdFromTask(task) === employeeId)
          .filter((task) => {
            const effectiveStatus = getLiveTaskStatus(task);
            const titleMatch = normalize(task.title).includes(search);
            const descMatch = normalize(task.description).includes(search);
            const woMatch = normalize(task.workOrder?.woNo).includes(search);
            const statusMatch = !filters.status || effectiveStatus === filters.status;
            const priorityMatch =
              !filters.priority || task.priority === filters.priority;
            const workOrderMatch =
              !filters.workOrder || getWorkOrderIdFromTask(task) === filters.workOrder;
            return (
              (!search || titleMatch || descMatch || woMatch) &&
              statusMatch &&
              priorityMatch &&
              workOrderMatch
            );
          });

        return {
          column: employee,
          tasks: employeeTasks,
        };
      })
      .sort((a, b) => {
        const aCount = a.tasks.length;
        const bCount = b.tasks.length;
        // 1. Employees with assigned tasks appear first
        if (aCount > 0 && bCount === 0) return -1;
        if (aCount === 0 && bCount > 0) return 1;
        // 2. Among those with tasks, sort by most tasks first
        if (aCount > 0 && bCount > 0 && bCount !== aCount) {
          return bCount - aCount;
        }
        // 3. For ties or 0-task employees, sort by name (Unassigned at start of group)
        const aIsUnassigned = a.column?._id === "unassigned";
        const bIsUnassigned = b.column?._id === "unassigned";
        if (aIsUnassigned) return -1;
        if (bIsUnassigned) return 1;
        return (a.column?.name || "").localeCompare(b.column?.name || "");
      });
  }, [currentUser, employees, filters, isStaffView, tasks]);

  // Employee grouped rows for Pending Overview table
  const pendingOverviewRows = useMemo(() => {
    const search = normalize(filters.search);

    const baseEmployeeList = [
      ...employees,
      {
        _id: "unassigned",
        name: "Unassigned Tasks",
        role: "Backlog",
        department: "Unassigned",
      },
    ];

    const rows = baseEmployeeList
      .map((emp) => {
        const empId = getId(emp);
        const empTasks = tasks.filter((t) => getEmployeeIdFromTask(t) === empId);

        const filteredTasks = empTasks.filter((task) => {
          const effectiveStatus = getLiveTaskStatus(task);
          const titleMatch = normalize(task.title).includes(search);
          const descMatch = normalize(task.description).includes(search);
          const woMatch = normalize(task.workOrder?.woNo).includes(search);
          const empMatch = normalize(emp.name).includes(search);
          const statusMatch = !filters.status || effectiveStatus === filters.status;
          const priorityMatch = !filters.priority || task.priority === filters.priority;
          const workOrderMatch =
            !filters.workOrder || getWorkOrderIdFromTask(task) === filters.workOrder;

          return (
            (!search || titleMatch || descMatch || woMatch || empMatch) &&
            statusMatch &&
            priorityMatch &&
            workOrderMatch
          );
        });

        const pendingTasks = filteredTasks.filter((t) => normalize(t.status) !== "completed");
        const overdueTasks = filteredTasks.filter(isTaskOverdue);
        const highPriorityTasks = filteredTasks.filter(
          (t) =>
            normalize(t.status) !== "completed" &&
            ["high", "critical"].includes(normalize(t.priority))
        );

        const nextDueInfo = getNextDueInfo(filteredTasks);

        return {
          employee: emp,
          employeeId: empId,
          name: emp.name || "Unassigned",
          department: emp.department || emp.role?.name || emp.role || "Team Member",
          totalTasks: filteredTasks.length,
          pendingCount: pendingTasks.length,
          overdueCount: overdueTasks.length,
          highPriorityCount: highPriorityTasks.length,
          nextDue: nextDueInfo,
          tasks: filteredTasks,
        };
      })
      .filter((row) => {
        // If searching and employee has 0 matching tasks and name doesn't match search, filter out
        if (filters.search) {
          const nameMatch = normalize(row.name).includes(search);
          return nameMatch || row.tasks.length > 0;
        }
        // Don't show unassigned row if 0 tasks in backlog
        if (row.employeeId === "unassigned" && row.totalTasks === 0) return false;
        return true;
      });

    // Sorting
    return rows.sort((a, b) => {
      if (sortBy === "overdue") {
        if (b.overdueCount !== a.overdueCount) return b.overdueCount - a.overdueCount;
        if (b.pendingCount !== a.pendingCount) return b.pendingCount - a.pendingCount;
        return a.name.localeCompare(b.name);
      }
      if (sortBy === "mostTasks") {
        return b.pendingCount - a.pendingCount;
      }
      if (sortBy === "nameAsc") {
        return a.name.localeCompare(b.name);
      }
      if (sortBy === "dueSoon") {
        if (b.overdueCount !== a.overdueCount) return b.overdueCount - a.overdueCount;
        return b.highPriorityCount - a.highPriorityCount;
      }
      return 0;
    });
  }, [employees, tasks, filters, sortBy]);

  // Dedicated single-employee inspection data
  const selectedEmployeeObj = useMemo(() => {
    if (!selectedEmployeeId) return null;
    if (selectedEmployeeId === "unassigned") {
      return {
        _id: "unassigned",
        name: "Unassigned Tasks",
        department: "Backlog",
        role: { name: "Unassigned Tasks" },
      };
    }
    return employees.find((emp) => getId(emp) === selectedEmployeeId) || null;
  }, [selectedEmployeeId, employees]);

  const selectedEmployeeTasks = useMemo(() => {
    if (!selectedEmployeeId) return [];
    return tasks.filter((t) => getEmployeeIdFromTask(t) === selectedEmployeeId);
  }, [selectedEmployeeId, tasks]);

  const selectedEmployeeKpi = useMemo(() => {
    if (!selectedEmployeeId) return null;
    const allEmpTasks = selectedEmployeeTasks;
    const pendingTasks = allEmpTasks.filter((t) => normalize(t.status) !== "completed");
    const overdueTasks = allEmpTasks.filter(isTaskOverdue);
    const highPriorityTasks = allEmpTasks.filter(
      (t) =>
        normalize(t.status) !== "completed" &&
        ["high", "critical"].includes(normalize(t.priority))
    );
    const completedTasks = allEmpTasks.filter((t) => normalize(t.status) === "completed");

    return {
      total: allEmpTasks.length,
      pending: pendingTasks.length,
      overdue: overdueTasks.length,
      highPriority: highPriorityTasks.length,
      completed: completedTasks.length,
    };
  }, [selectedEmployeeId, selectedEmployeeTasks]);

  const selectedEmployeeColumns = useMemo(() => {
    if (!selectedEmployeeId) return [];
    const search = normalize(filters.search);

    const filtered = selectedEmployeeTasks.filter((task) => {
      const effectiveStatus = getLiveTaskStatus(task);
      const titleMatch = normalize(task.title).includes(search);
      const descMatch = normalize(task.description).includes(search);
      const woMatch = normalize(task.workOrder?.woNo).includes(search);
      const statusMatch = !filters.status || effectiveStatus === filters.status;
      const priorityMatch = !filters.priority || task.priority === filters.priority;
      const workOrderMatch =
        !filters.workOrder || getWorkOrderIdFromTask(task) === filters.workOrder;

      return (
        (!search || titleMatch || descMatch || woMatch) &&
        statusMatch &&
        priorityMatch &&
        workOrderMatch
      );
    });

    return staffColumnOrder
      .map((statusName) => ({
        column: {
          _id: statusName,
          name: statusName,
          role: "Task status",
        },
        tasks: filtered.filter((task) => getStaffStatusColumn(getLiveTaskStatus(task)) === statusName),
      }))
      .filter((column) => !filters.status || column.column.name === filters.status);
  }, [selectedEmployeeId, selectedEmployeeTasks, filters]);

  const totalVisibleTasks = boardColumns.reduce(
    (sum, column) => sum + column.tasks.length,
    0
  );

  const toggleEmployeeExpand = (empId) => {
    setExpandedEmployeeIds((prev) =>
      prev.includes(empId) ? prev.filter((id) => id !== empId) : [...prev, empId]
    );
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((current) => ({ ...current, [name]: value }));
    if (name === "workOrder" && !isStaffView) {
      fetchAssignableUsers(value)
        .then((assignableUsers) => {
          setEmployees(assignableUsers);
          setFilters((current) => ({ ...current, employee: "" }));
        })
        .catch((error) => {
          toast.error(error.response?.data?.message || "Failed to load assignable users");
        });
    }
  };

  const resetFilters = () => {
    setFilters({
      search: "",
      status: "",
      priority: "",
      employee: "",
      workOrder: "",
    });
  };

  const openTaskModal = (prefillEmployeeId = "") => {
    const nextWorkOrder = filters.workOrder || workOrderFromUrl;
    const assignEmp =
      prefillEmployeeId ||
      (selectedEmployeeId && selectedEmployeeId !== "unassigned"
        ? selectedEmployeeId
        : filters.employee === "unassigned"
        ? ""
        : filters.employee);
    setTaskForm({
      workOrder: nextWorkOrder,
      assignedEmployeeId: assignEmp || "",
      title: "",
      description: "",
      priority: "Medium",
      estimatedHours: 2,
      plannedEndDate: "",
    });
    fetchAssignableUsers(nextWorkOrder)
      .then(setEmployees)
      .catch((error) => {
        toast.error(error.response?.data?.message || "Failed to load assignable users");
      });
    setTaskModalOpen(true);
  };

  const handleTaskFormChange = (event) => {
    const { name, value } = event.target;
    setTaskForm((current) => ({
      ...current,
      [name]: value,
      ...(name === "workOrder" ? { assignedEmployeeId: "" } : {}),
    }));
    if (name === "workOrder") {
      fetchAssignableUsers(value)
        .then(setEmployees)
        .catch((error) => {
          toast.error(error.response?.data?.message || "Failed to load assignable users");
        });
    }
  };

  const handleEditTaskFormChange = (event) => {
    const { name, value } = event.target;
    setEditTaskForm((current) => ({
      ...current,
      [name]: value,
      ...(name === "workOrder" ? { assignedEmployeeId: "" } : {}),
    }));
    if (name === "workOrder") {
      fetchAssignableUsers(value)
        .then(setEmployees)
        .catch((error) => {
          toast.error(error.response?.data?.message || "Failed to load assignable users");
        });
    }
  };

  const openTaskDetails = (task) => {
    setSelectedTask({
      ...task,
      workOrder: workOrders.find((wo) => getId(wo) === getWorkOrderIdFromTask(task)) || task.workOrder,
      employeeName:
        employees.find((emp) => getId(emp) === getEmployeeIdFromTask(task))?.name ||
        task.employeeName ||
        "Unassigned",
    });
  };

  const openEditTaskModal = (task) => {
    const assignedId = getEmployeeIdFromTask(task);
    const workOrderId = getWorkOrderIdFromTask(task);
    setEditTaskId(getId(task));
    setEditTaskForm({
      workOrder: workOrderId,
      assignedEmployeeId: assignedId === "unassigned" ? "" : assignedId,
      title: task.title || "",
      description: task.description || "",
      priority: task.priority || "Medium",
      status: task.status || "Unassigned",
      estimatedHours: task.estimatedHours || 2,
      plannedEndDate: formatInputDate(task.plannedEndDate || task.dueDate),
    });
    fetchAssignableUsers(workOrderId)
      .then(setEmployees)
      .catch((error) => {
        toast.error(error.response?.data?.message || "Failed to load assignable users");
      });
    setEditTaskModalOpen(true);
  };

  const openDeleteTaskModal = (task) => {
    setDeleteTaskCandidate(task);
  };

  const handleCreateTask = async (event) => {
    event.preventDefault();
    if (!taskForm.title.trim()) {
      toast.error("Task title is required");
      return;
    }

    setSavingTask(true);
    try {
      const payload = {
        title: taskForm.title.trim(),
        description: taskForm.description.trim(),
        priority: taskForm.priority,
        estimatedHours: Number(taskForm.estimatedHours) || 0,
        workOrder: taskForm.workOrder || undefined,
        assignedEmployee: taskForm.assignedEmployeeId || undefined,
        plannedEndDate: taskForm.plannedEndDate || undefined,
      };

      const response = await api.post("/tasks", payload);
      const createdTask = response.data?.data || response.data;
      toast.success("Task created successfully");
      setTaskModalOpen(false);
      fetchBoardData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create task");
    } finally {
      setSavingTask(false);
    }
  };

  const handleUpdateTask = async (event) => {
    event.preventDefault();
    if (!editTaskForm.title.trim()) {
      toast.error("Task title is required");
      return;
    }

    setSavingTask(true);
    try {
      const payload = {
        title: editTaskForm.title.trim(),
        description: editTaskForm.description.trim(),
        priority: editTaskForm.priority,
        status: editTaskForm.status,
        estimatedHours: Number(editTaskForm.estimatedHours) || 0,
        workOrder: editTaskForm.workOrder || undefined,
        assignedEmployee: editTaskForm.assignedEmployeeId || undefined,
        plannedEndDate: editTaskForm.plannedEndDate || undefined,
      };

      await api.patch(`/tasks/${editTaskId}`, payload);
      toast.success("Task updated successfully");
      setEditTaskModalOpen(false);
      fetchBoardData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update task");
    } finally {
      setSavingTask(false);
    }
  };

  const handleDeleteTask = async () => {
    if (!deleteTaskCandidate) return;
    setSavingTask(true);
    try {
      await api.delete(`/tasks/${getId(deleteTaskCandidate)}`);
      toast.success("Task deleted successfully");
      setDeleteTaskCandidate(null);
      fetchBoardData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete task");
    } finally {
      setSavingTask(false);
    }
  };

  const handleAddComment = async (event) => {
    event.preventDefault();
    if (!commentText.trim() || !selectedTask) return;

    setSavingComment(true);
    try {
      const response = await api.post(`/tasks/${getId(selectedTask)}/comments`, {
        comment: commentText.trim(),
      });
      const updatedComments = response.data?.data?.comments || response.data?.comments || [];
      setSelectedTask((current) =>
        current ? { ...current, comments: updatedComments } : current
      );
      setCommentText("");
      toast.success("Comment added");
      fetchBoardData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add comment");
    } finally {
      setSavingComment(false);
    }
  };

  const handleDragStart = (taskId) => {
    setDraggedTaskId(taskId);
  };

  const handleDrop = async (targetEmployeeId) => {
    if (!draggedTaskId) return;

    if (isStaffView || selectedEmployeeId) {
      const targetStatus = staffColumnStatusPayload[targetEmployeeId];
      if (!targetStatus) return;

      setDraggedTaskId(null);
      setActiveColumnId(null);

      const previousTasks = tasks;
      setTasks((currentTasks) =>
        currentTasks.map((item) =>
          getId(item) === draggedTaskId ? { ...item, status: targetStatus } : item
        )
      );

      try {
        await api.patch(`/tasks/${draggedTaskId}/status`, { status: targetStatus });
        toast.success(`Task moved to ${targetStatus}`);
      } catch (error) {
        setTasks(previousTasks);
        toast.error(error.response?.data?.message || "Status update failed");
      }
      return;
    }

    const task = tasks.find((item) => getId(item) === draggedTaskId);
    if (!task) return;

    const previousEmployeeId = getEmployeeIdFromTask(task);
    setDraggedTaskId(null);
    setActiveColumnId(null);

    if (previousEmployeeId === targetEmployeeId) return;

    const previousTasks = tasks;
    setTasks((currentTasks) =>
      currentTasks.map((item) =>
        getId(item) === draggedTaskId
          ? {
              ...item,
              employeeUserId: targetEmployeeId === "unassigned" ? "" : targetEmployeeId,
              employeeId: targetEmployeeId === "unassigned" ? "" : targetEmployeeId,
              assignedEmployeeId: targetEmployeeId === "unassigned" ? "" : targetEmployeeId,
              assignedEmployee: targetEmployeeId === "unassigned" ? "" : targetEmployeeId,
              employeeName:
                targetEmployeeId === "unassigned"
                  ? "Unassigned"
                  : employees.find((employee) => getId(employee) === targetEmployeeId)?.name,
            }
          : item
      )
    );

    try {
      const payload = {
        assignedEmployee: targetEmployeeId === "unassigned" ? "" : targetEmployeeId,
      };

      await api.patch(`/tasks/${draggedTaskId}`, payload);

      const employeeName =
        targetEmployeeId === "unassigned"
          ? "Unassigned"
          : employees.find((employee) => getId(employee) === targetEmployeeId)?.name ||
            "employee";
      toast.success(`Task assigned to ${employeeName}`);
    } catch (error) {
      setTasks(previousTasks);
      toast.error(error.response?.data?.message || "Task transfer failed");
    }
  };

  const statuses = useMemo(() => {
    if (isStaffView) return staffColumnOrder;
    return Array.from(new Set(tasks.map((task) => getLiveTaskStatus(task)).filter(Boolean)));
  }, [isStaffView, tasks]);

  const workOrderOptions = useMemo(() => {
    const options = new Map();
    workOrders.forEach((workOrder) => {
      const workOrderId = getId(workOrder);
      if (!workOrderId) return;
      const label = `${workOrder.woNo || workOrder.woNumber || workOrderId}${
        workOrder.project?.projectName ? ` - ${workOrder.project.projectName}` : ""
      }`;
      options.set(workOrderId, label);
    });
    tasks.forEach((task) => {
      const workOrderId = getWorkOrderIdFromTask(task);
      if (!workOrderId || options.has(workOrderId)) return;
      const label = task.workOrder?.woNo || task.workOrder?.woNumber || workOrderId;
      options.set(workOrderId, label);
    });
    return Array.from(options, ([value, label]) => ({ value, label }));
  }, [tasks, workOrders]);

  const viewTitle = isStaffView
    ? "My Tasks"
    : selectedEmployeeId
    ? `${selectedEmployeeObj?.name || "Employee"} — Tasks & Overview`
    : boardView === "pending"
    ? "Tasks — Pending Overview"
    : boardView === "kanban"
    ? "Tasks — Kanban Board"
    : "Tasks — Reporting Tree";

  const viewSubtitle = isStaffView
    ? `${tasks.length} assigned task${tasks.length === 1 ? "" : "s"} · drag & drop or click to update status`
    : selectedEmployeeId
    ? `${selectedEmployeeTasks.length} task${selectedEmployeeTasks.length === 1 ? "" : "s"} · ${selectedEmployeeObj?.department || selectedEmployeeObj?.role?.name || "Team Member"}`
    : `${employees.length} employees · ${tasks.length} total tasks · sorted by ${
        sortBy === "overdue"
          ? "overdue first"
          : sortBy === "mostTasks"
          ? "most tasks"
          : sortBy === "dueSoon"
          ? "due date"
          : "name A-Z"
      }`;

  return (
    <div className="admin-task-kanban">
      {/* ======================= TOP HEADER & VIEW TABS ======================= */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="tasks-main-title mb-1">{viewTitle}</h2>
          <div className="tasks-main-subtitle text-muted">{viewSubtitle}</div>
        </div>

        <div className="d-flex align-items-center flex-wrap gap-2">
          {/* Main Tab Pill Switcher (Admin / Management only) */}
          {!isStaffView && (
            <div className="tasks-nav-pill-group">
              <button
                type="button"
                className={`tasks-nav-pill-btn ${boardView === "pending" && !selectedEmployeeId ? "active" : ""}`}
                onClick={() => {
                  setBoardView("pending");
                  setSelectedEmployeeId(null);
                }}
              >
                Pending Overview
              </button>
              <button
                type="button"
                className={`tasks-nav-pill-btn ${boardView === "kanban" ? "active" : ""}`}
                onClick={() => {
                  setBoardView("kanban");
                  setSelectedEmployeeId(null);
                }}
              >
                Kanban Board
              </button>
              {canManageReportingTree && (
                <button
                  type="button"
                  className={`tasks-nav-pill-btn ${boardView === "reporting" ? "active" : ""}`}
                  onClick={() => {
                    setBoardView("reporting");
                    setSelectedEmployeeId(null);
                  }}
                >
                  Reporting Tree
                </button>
              )}
            </div>
          )}

          {!isStaffView && (
            <button className="btn btn-primary btn-sm ms-2" onClick={() => openTaskModal(selectedEmployeeId)}>
              <i className="fas fa-plus me-1" />
              New Task
            </button>
          )}
          <button className="btn btn-outline-secondary btn-sm" onClick={fetchBoardData} title="Refresh">
            <i className="fas fa-sync-alt" />
          </button>
        </div>
      </div>

      {/* ======================= VIEW 1: PENDING OVERVIEW ======================= */}
      {loading ? (
        <div className="tasks-loading-box">
          <div className="spinner-border text-primary" role="status" />
          <div className="mt-2 text-muted small">Loading tasks data...</div>
        </div>
      ) : boardView === "pending" ? (
        selectedEmployeeId ? (
          /* ---------- 1A. DEDICATED EMPLOYEE INSPECTION VIEW ---------- */
          <div className="tasks-employee-inspection-zone">
            {/* Employee Top Banner */}
            <div className="tasks-emp-banner d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
              <div className="d-flex align-items-center gap-3">
                <button
                  type="button"
                  className="tasks-back-btn"
                  onClick={() => setSelectedEmployeeId(null)}
                >
                  <i className="fas fa-arrow-left" /> Back to Overview
                </button>
                <div className="tasks-avatar-circle" style={{ width: "42px", height: "42px", fontSize: "14px" }}>
                  {getInitials(selectedEmployeeObj?.name)}
                </div>
                <div>
                  <h4 className="mb-0" style={{ fontWeight: 800 }}>{selectedEmployeeObj?.name || "Employee"}</h4>
                  <span className="text-muted small">
                    {selectedEmployeeObj?.department || selectedEmployeeObj?.role?.name || "Team Member"}
                    {selectedEmployeeObj?.email ? ` · ${selectedEmployeeObj.email}` : ""}
                  </span>
                </div>
              </div>

              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => openTaskModal(selectedEmployeeId)}
                >
                  <i className="fas fa-plus me-1" />
                  Add Task for {selectedEmployeeObj?.name?.split(" ")[0] || "Employee"}
                </button>
              </div>
            </div>

            {/* 5 Employee KPI Metric Cards */}
            <div className="tasks-kpi-grid-5 mb-4">
              <div className="tasks-kpi-card">
                <div className="tasks-kpi-num tasks-kpi-navy">{selectedEmployeeKpi?.total || 0}</div>
                <div className="tasks-kpi-lbl">Total assigned</div>
              </div>
              <div className="tasks-kpi-card">
                <div className="tasks-kpi-num tasks-kpi-blue">{selectedEmployeeKpi?.pending || 0}</div>
                <div className="tasks-kpi-lbl">Pending tasks</div>
              </div>
              <div className="tasks-kpi-card">
                <div className="tasks-kpi-num tasks-kpi-red">{selectedEmployeeKpi?.overdue || 0}</div>
                <div className="tasks-kpi-lbl">Overdue tasks</div>
              </div>
              <div className="tasks-kpi-card">
                <div className="tasks-kpi-num tasks-kpi-amber">{selectedEmployeeKpi?.highPriority || 0}</div>
                <div className="tasks-kpi-lbl">High priority open</div>
              </div>
              <div className="tasks-kpi-card">
                <div className="tasks-kpi-num tasks-kpi-green">{selectedEmployeeKpi?.completed || 0}</div>
                <div className="tasks-kpi-lbl">Completed</div>
              </div>
            </div>

            {/* Search & Filter Bar for this employee */}
            <div className="tasks-filter-bar mb-4">
              <div className="row g-2 align-items-center">
                <div className="col-lg-6 col-md-12">
                  <div className="tasks-search-wrap">
                    <i className="fas fa-search tasks-search-icon" />
                    <input
                      className="form-control tasks-search-input"
                      name="search"
                      placeholder={`Search ${selectedEmployeeObj?.name || "employee"}'s tasks...`}
                      value={filters.search}
                      onChange={handleFilterChange}
                    />
                    {filters.search && (
                      <button
                        type="button"
                        className="tasks-search-clear"
                        onClick={() => setFilters((c) => ({ ...c, search: "" }))}
                      >
                        <i className="fas fa-times" />
                      </button>
                    )}
                  </div>
                </div>

                <div className="col-lg-3 col-md-6 col-6">
                  <select
                    className="form-select form-select-sm tasks-select"
                    name="workOrder"
                    value={filters.workOrder}
                    onChange={handleFilterChange}
                  >
                    <option value="">All Work Orders</option>
                    {workOrderOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-lg-3 col-md-6 col-6">
                  <select
                    className="form-select form-select-sm tasks-select"
                    name="priority"
                    value={filters.priority}
                    onChange={handleFilterChange}
                  >
                    <option value="">All Priority</option>
                    <option>Critical</option>
                    <option>High</option>
                    <option>Medium</option>
                    <option>Low</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Dedicated Status Kanban Columns for this employee */}
            <div className="kanban-board">
              {selectedEmployeeColumns.map(({ column, tasks: columnTasks }) => {
                const columnId = getId(column);
                return (
                  <div
                    key={columnId}
                    className={`employee-column ${
                      activeColumnId === columnId ? "drop-active" : ""
                    }`}
                    onDragOver={(event) => {
                      event.preventDefault();
                      setActiveColumnId(columnId);
                    }}
                    onDragLeave={() => setActiveColumnId(null)}
                    onDrop={() => handleDrop(columnId)}
                  >
                    <div className="employee-header">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-2">
                          <span className="employee-avatar" style={{ fontSize: "11px", width: "28px", height: "28px" }}>
                            {column.name[0]}
                          </span>
                          <h6 className="mb-0">{column.name}</h6>
                        </div>
                        <span className="badge bg-light text-dark border">
                          {columnTasks.length}
                        </span>
                      </div>
                    </div>

                    <div className="task-list">
                      {columnTasks.length === 0 ? (
                        <div className="empty-drop-zone">
                          <div>
                            <div>No tasks in {column.name}</div>
                            <small>Drop task here to update status</small>
                          </div>
                        </div>
                      ) : (
                        columnTasks.map((task) => {
                          const taskId = getId(task);
                          const effectiveStatus = getLiveTaskStatus(task);
                          const overdue = isTaskOverdue(task);
                          return (
                            <div
                              key={taskId}
                              className={`task-card ${
                                draggedTaskId === taskId ? "dragging" : ""
                              } ${overdue ? "task-card-overdue-border" : ""}`}
                              draggable
                              onDragStart={() => handleDragStart(taskId)}
                              onDragEnd={() => {
                                setDraggedTaskId(null);
                                setActiveColumnId(null);
                              }}
                              onClick={() => openTaskDetails(task)}
                            >
                              <div className="d-flex justify-content-between gap-2">
                                <h6 className="mb-1">{task.title || "Untitled Task"}</h6>
                                <div className="d-flex align-items-start gap-1">
                                  <button
                                    type="button"
                                    className="task-card-icon-btn"
                                    title="Edit task"
                                    aria-label="Edit task"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      openEditTaskModal(task);
                                    }}
                                  >
                                    <i className="fas fa-edit" />
                                  </button>
                                  <button
                                    type="button"
                                    className="task-card-icon-btn task-card-icon-btn-danger"
                                    title="Delete task"
                                    aria-label="Delete task"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      openDeleteTaskModal(task);
                                    }}
                                    disabled={savingTask}
                                  >
                                    <i className="fas fa-trash" />
                                  </button>
                                </div>
                              </div>
                              {task.description && (
                                <p className="text-muted small mb-2 text-truncate">
                                  {task.description}
                                </p>
                              )}
                              <div className="d-flex flex-wrap gap-2 mb-2">
                                <span className={`priority-badge ${priorityClass(task.priority)}`}>
                                  {task.priority || "Medium"}
                                </span>
                                <span className={`status-badge ${statusClass(effectiveStatus)}`}>
                                  {effectiveStatus}
                                </span>
                              </div>
                              <div className="small text-muted d-flex justify-content-between align-items-center">
                                <div>
                                  {task.workOrder?.woNo && <div>WO: {task.workOrder.woNo}</div>}
                                  <div>Due: {formatDate(task.plannedEndDate || task.dueDate)}</div>
                                </div>
                                {(task.comments || []).length > 0 && (
                                  <div className="text-primary">
                                    <i className="fas fa-comment-dots me-1" />
                                    {task.comments.length}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          /* ---------- 1B. ALL-EMPLOYEES OVERVIEW LIST ---------- */
          <>
            {/* 4 TOP KPI METRIC CARDS */}
            <div className="tasks-kpi-grid mb-4">
              <div className="tasks-kpi-card">
                <div className="tasks-kpi-num tasks-kpi-red">{kpiData.overdueTasksCount}</div>
                <div className="tasks-kpi-lbl">Overdue tasks</div>
              </div>
              <div className="tasks-kpi-card">
                <div className="tasks-kpi-num tasks-kpi-amber">{kpiData.highPriorityCount}</div>
                <div className="tasks-kpi-lbl">High priority open</div>
              </div>
              <div className="tasks-kpi-card">
                <div className="tasks-kpi-num tasks-kpi-navy">{kpiData.totalPendingCount}</div>
                <div className="tasks-kpi-lbl">Total pending</div>
              </div>
              <div className="tasks-kpi-card">
                <div className="tasks-kpi-num tasks-kpi-navy">{kpiData.employeesWithOverdueCount}</div>
                <div className="tasks-kpi-lbl">Employees with overdue work</div>
              </div>
            </div>

            {/* SEARCH & FILTER BAR */}
            <div className="tasks-filter-bar mb-4">
              <div className="row g-2 align-items-center">
                <div className="col-lg-5 col-md-12">
                  <div className="tasks-search-wrap">
                    <i className="fas fa-search tasks-search-icon" />
                    <input
                      className="form-control tasks-search-input"
                      name="search"
                      placeholder="Search employee, task or work order"
                      value={filters.search}
                      onChange={handleFilterChange}
                    />
                    {filters.search && (
                      <button
                        type="button"
                        className="tasks-search-clear"
                        onClick={() => setFilters((c) => ({ ...c, search: "" }))}
                      >
                        <i className="fas fa-times" />
                      </button>
                    )}
                  </div>
                </div>

                <div className="col-lg-2 col-md-4 col-6">
                  <select
                    className="form-select form-select-sm tasks-select"
                    name="workOrder"
                    value={filters.workOrder}
                    onChange={handleFilterChange}
                  >
                    <option value="">All Work Orders</option>
                    {workOrderOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-lg-2 col-md-4 col-6">
                  <select
                    className="form-select form-select-sm tasks-select"
                    name="status"
                    value={filters.status}
                    onChange={handleFilterChange}
                  >
                    <option value="">All Status</option>
                    {statuses.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-lg-1 col-md-4 col-6">
                  <select
                    className="form-select form-select-sm tasks-select"
                    name="priority"
                    value={filters.priority}
                    onChange={handleFilterChange}
                  >
                    <option value="">All Priority</option>
                    <option>Critical</option>
                    <option>High</option>
                    <option>Medium</option>
                    <option>Low</option>
                  </select>
                </div>

                <div className="col-lg-2 col-md-4 col-6">
                  <select
                    className="form-select form-select-sm tasks-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="overdue">Sorted: Overdue → Due date</option>
                    <option value="mostTasks">Sorted: Most Tasks</option>
                    <option value="dueSoon">Sorted: Due Soon</option>
                    <option value="nameAsc">Sorted: Name A-Z</option>
                  </select>
                </div>
              </div>
            </div>

            {/* OVERVIEW TABLE */}
            <div className="tasks-table-card">
              <table className="tasks-table">
                <thead>
                  <tr>
                    <th style={{ width: "35%" }}>Employee</th>
                    <th className="text-center" style={{ width: "13%" }}>
                      Pending
                    </th>
                    <th className="text-center" style={{ width: "13%" }}>
                      Overdue
                    </th>
                    <th className="text-center" style={{ width: "13%" }}>
                      High priority
                    </th>
                    <th style={{ width: "20%" }}>Next due</th>
                    <th className="text-end" style={{ width: "6%" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingOverviewRows.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-5 text-muted">
                        No employees or tasks found matching your filter criteria.
                      </td>
                    </tr>
                  ) : (
                    pendingOverviewRows.map((row) => (
                      <tr key={row.employeeId} className="tasks-row-group">
                        <td colSpan="6" className="p-0 border-0">
                          <div
                            className="tasks-row-main"
                            onClick={() => setSelectedEmployeeId(row.employeeId)}
                            title={`Click to view ${row.name}'s Kanban board & Overview`}
                          >
                            <div style={{ width: "35%" }} className="d-flex align-items-center gap-3">
                              <div className="tasks-avatar-circle">{getInitials(row.name)}</div>
                              <div className="min-w-0">
                                <div className="tasks-emp-name">{row.name}</div>
                                <div className="tasks-emp-sub">{row.department}</div>
                              </div>
                            </div>

                            <div
                              style={{ width: "13%" }}
                              className="text-center tasks-num-cell"
                            >
                              {row.pendingCount}
                            </div>

                            <div
                              style={{ width: "13%" }}
                              className={`text-center tasks-num-cell ${
                                row.overdueCount > 0 ? "tasks-overdue-val" : "text-muted"
                              }`}
                            >
                              {row.overdueCount}
                            </div>

                            <div
                              style={{ width: "13%" }}
                              className={`text-center tasks-num-cell ${
                                row.highPriorityCount > 0 ? "tasks-high-val" : "text-muted"
                              }`}
                            >
                              {row.highPriorityCount}
                            </div>

                            <div style={{ width: "20%" }}>
                              {row.nextDue.dateStr === "—" ? (
                                <span className="text-muted">—</span>
                              ) : (
                                <div>
                                  <div className="tasks-due-date">{row.nextDue.dateStr}</div>
                                  {row.nextDue.statusText && (
                                    <div
                                      className={`tasks-due-sub ${
                                        row.nextDue.statusType === "overdue"
                                          ? "tasks-due-overdue"
                                          : "tasks-due-soon"
                                      }`}
                                    >
                                      {row.nextDue.statusText}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            <div style={{ width: "6%" }} className="text-end">
                              <span className="btn btn-sm btn-outline-primary py-1 px-2" style={{ fontSize: "11px", whiteSpace: "nowrap" }}>
                                View <i className="fas fa-arrow-right ms-1" />
                              </span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )
      ) : boardView === "kanban" ? (
        /* ======================= VIEW 2: GLOBAL MULTI-EMPLOYEE KANBAN BOARD ======================= */
        <>
          {/* SEARCH & FILTER BAR FOR GLOBAL KANBAN */}
          <div className="tasks-filter-bar mb-4">
            <div className="row g-2 align-items-center">
              <div className="col-lg-6 col-md-12">
                <div className="tasks-search-wrap">
                  <i className="fas fa-search tasks-search-icon" />
                  <input
                    className="form-control tasks-search-input"
                    name="search"
                    placeholder="Search employee, task or work order"
                    value={filters.search}
                    onChange={handleFilterChange}
                  />
                  {filters.search && (
                    <button
                      type="button"
                      className="tasks-search-clear"
                      onClick={() => setFilters((c) => ({ ...c, search: "" }))}
                    >
                      <i className="fas fa-times" />
                    </button>
                  )}
                </div>
              </div>

              <div className="col-lg-3 col-md-6 col-6">
                <select
                  className="form-select form-select-sm tasks-select"
                  name="workOrder"
                  value={filters.workOrder}
                  onChange={handleFilterChange}
                >
                  <option value="">All Work Orders</option>
                  {workOrderOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-lg-3 col-md-6 col-6">
                <select
                  className="form-select form-select-sm tasks-select"
                  name="priority"
                  value={filters.priority}
                  onChange={handleFilterChange}
                >
                  <option value="">All Priority</option>
                  <option>Critical</option>
                  <option>High</option>
                  <option>Medium</option>
                  <option>Low</option>
                </select>
              </div>
            </div>
          </div>

          <div className="kanban-board">
            {boardColumns.map(({ column, tasks: columnTasks }) => {
              const columnId = getId(column);
              return (
                <div
                  key={columnId}
                  className={`employee-column ${
                    activeColumnId === columnId ? "drop-active" : ""
                  }`}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setActiveColumnId(columnId);
                  }}
                  onDragLeave={() => setActiveColumnId(null)}
                  onDrop={() => handleDrop(columnId)}
                >
                  <div className="employee-header">
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center gap-2">
                        <span className="employee-avatar">{getInitials(column.name)}</span>
                        <div className="min-w-0">
                          <h6 className="mb-0 text-truncate">{column.name}</h6>
                          <small className="text-muted">
                            {column.role?.name || column.role || column.department || "Task group"}
                          </small>
                        </div>
                      </div>
                      {!isStaffView && columnId !== "unassigned" && (
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm py-0 px-2"
                          style={{ fontSize: "11px" }}
                          onClick={() => {
                            setBoardView("pending");
                            setSelectedEmployeeId(columnId);
                          }}
                          title={`Open ${column.name}'s dedicated board`}
                        >
                          View
                        </button>
                      )}
                    </div>
                    <div className="d-flex justify-content-between mt-3 small">
                      <span>{columnTasks.length} tasks</span>
                      <span>
                        {
                          columnTasks.filter(
                            (task) => normalize(task.status) !== "completed"
                          ).length
                        }{" "}
                        open
                      </span>
                    </div>
                  </div>

                  <div className="task-list">
                    {columnTasks.length === 0 ? (
                      <div className="empty-drop-zone">
                        <div>
                          <div>No tasks</div>
                          {!isStaffView && <small>Drop task here</small>}
                        </div>
                      </div>
                    ) : (
                      columnTasks.map((task) => {
                        const taskId = getId(task);
                        const effectiveStatus = getLiveTaskStatus(task);
                        return (
                          <div
                            key={taskId}
                            className={`task-card ${
                              draggedTaskId === taskId ? "dragging" : ""
                            }`}
                            draggable
                            onDragStart={() => handleDragStart(taskId)}
                            onDragEnd={() => {
                              setDraggedTaskId(null);
                              setActiveColumnId(null);
                            }}
                            onClick={() => openTaskDetails(task)}
                          >
                            <div className="d-flex justify-content-between gap-2">
                              <h6 className="mb-1">{task.title || "Untitled Task"}</h6>
                              <div className="d-flex align-items-start gap-2">
                                {!isStaffView && (
                                  <>
                                    <button
                                      type="button"
                                      className="task-card-icon-btn"
                                      title="Edit task"
                                      aria-label="Edit task"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        openEditTaskModal(task);
                                      }}
                                    >
                                      <i className="fas fa-edit" />
                                    </button>
                                    <button
                                      type="button"
                                      className="task-card-icon-btn task-card-icon-btn-danger"
                                      title="Delete task"
                                      aria-label="Delete task"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        openDeleteTaskModal(task);
                                      }}
                                      disabled={savingTask}
                                    >
                                      <i className="fas fa-trash" />
                                    </button>
                                  </>
                                )}
                                <i className="fas fa-grip-vertical text-muted mt-1" />
                              </div>
                            </div>
                            <p className="text-muted small mb-2">
                              {task.description || "No description"}
                            </p>
                            <div className="d-flex flex-wrap gap-2 mb-2">
                              <span
                                className={`priority-badge ${priorityClass(task.priority)}`}
                              >
                                {task.priority || "Medium"}
                              </span>
                              <span className={`status-badge ${statusClass(effectiveStatus)}`}>
                                {effectiveStatus}
                              </span>
                            </div>
                            <div className="small text-muted">
                              <div>WO: {task.workOrder?.woNo || "Other"}</div>
                              <div>Due: {formatDate(task.plannedEndDate || task.dueDate)}</div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : boardView === "kanban" ? (
        /* ======================= VIEW 2: KANBAN BOARD ======================= */
        <div className="kanban-board">
          {boardColumns.map(({ column, tasks: columnTasks }) => {
            const columnId = getId(column);
            return (
              <div
                key={columnId}
                className={`employee-column ${
                  activeColumnId === columnId ? "drop-active" : ""
                }`}
                onDragOver={(event) => {
                  event.preventDefault();
                  setActiveColumnId(columnId);
                }}
                onDragLeave={() => setActiveColumnId(null)}
                onDrop={() => handleDrop(columnId)}
              >
                <div className="employee-header">
                  <div className="d-flex align-items-center gap-2">
                    <span className="employee-avatar">{getInitials(column.name)}</span>
                    <div className="min-w-0">
                      <h6 className="mb-0 text-truncate">{column.name}</h6>
                      <small className="text-muted">
                        {column.role?.name || column.role || column.department || "Task group"}
                      </small>
                    </div>
                  </div>
                  <div className="d-flex justify-content-between mt-3 small">
                    <span>{columnTasks.length} tasks</span>
                    <span>
                      {
                        columnTasks.filter(
                          (task) => normalize(task.status) !== "completed"
                        ).length
                      }{" "}
                      open
                    </span>
                  </div>
                </div>

                <div className="task-list">
                  {columnTasks.length === 0 ? (
                    <div className="empty-drop-zone">
                      <div>
                        <div>No tasks</div>
                        {!isStaffView && <small>Drop task here</small>}
                      </div>
                    </div>
                  ) : (
                    columnTasks.map((task) => {
                      const taskId = getId(task);
                      const effectiveStatus = getLiveTaskStatus(task);
                      return (
                        <div
                          key={taskId}
                          className={`task-card ${
                            draggedTaskId === taskId ? "dragging" : ""
                          }`}
                          draggable
                          onDragStart={() => handleDragStart(taskId)}
                          onDragEnd={() => {
                            setDraggedTaskId(null);
                            setActiveColumnId(null);
                          }}
                          onClick={() => openTaskDetails(task)}
                        >
                          <div className="d-flex justify-content-between gap-2">
                            <h6 className="mb-1">{task.title || "Untitled Task"}</h6>
                            <div className="d-flex align-items-start gap-2">
                              {!isStaffView && (
                                <>
                                  <button
                                    type="button"
                                    className="task-card-icon-btn"
                                    title="Edit task"
                                    aria-label="Edit task"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      openEditTaskModal(task);
                                    }}
                                  >
                                    <i className="fas fa-edit" />
                                  </button>
                                  <button
                                    type="button"
                                    className="task-card-icon-btn task-card-icon-btn-danger"
                                    title="Delete task"
                                    aria-label="Delete task"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      openDeleteTaskModal(task);
                                    }}
                                    disabled={savingTask}
                                  >
                                    <i className="fas fa-trash" />
                                  </button>
                                </>
                              )}
                              <i className="fas fa-grip-vertical text-muted mt-1" />
                            </div>
                          </div>
                          <p className="text-muted small mb-2">
                            {task.description || "No description"}
                          </p>
                          <div className="d-flex flex-wrap gap-2 mb-2">
                            <span
                              className={`priority-badge ${priorityClass(task.priority)}`}
                            >
                              {task.priority || "Medium"}
                            </span>
                            <span className={`status-badge ${statusClass(effectiveStatus)}`}>
                              {effectiveStatus}
                            </span>
                          </div>
                          <div className="small text-muted">
                            <div>WO: {task.workOrder?.woNo || "Other"}</div>
                            <div>Due: {formatDate(task.plannedEndDate || task.dueDate)}</div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* ======================= VIEW 3: REPORTING TREE ======================= */
        <ReportingTreeManager />
      )}

      {/* ======================= TASK DETAILS MODAL ======================= */}
      <Modal
        show={Boolean(selectedTask)}
        onHide={() => {
          setSelectedTask(null);
          setCommentText("");
        }}
        centered
        dialogClassName="admin-task-kanban-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{selectedTask?.title || "Task Details"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="text-muted small mb-1">Description</label>
            <p className="mb-0">{selectedTask?.description || "No description"}</p>
          </div>
          <div className="row g-3">
            <div className="col-6">
              <label className="text-muted small mb-1">Priority</label>
              <div>{selectedTask?.priority || "Medium"}</div>
            </div>
            <div className="col-6">
              <label className="text-muted small mb-1">Status</label>
              <div>{selectedTask?.status || "Unassigned"}</div>
            </div>
            <div className="col-6">
              <label className="text-muted small mb-1">Work Order</label>
              <div>{selectedTask?.workOrder?.woNo || "Other"}</div>
            </div>
            <div className="col-6">
              <label className="text-muted small mb-1">Due Date</label>
              <div>{formatDate(selectedTask?.plannedEndDate || selectedTask?.dueDate)}</div>
            </div>
            <div className="col-6">
              <label className="text-muted small mb-1">Estimated Hours</label>
              <div>{selectedTask?.estimatedHours || "N/A"}</div>
            </div>
            <div className="col-6">
              <label className="text-muted small mb-1">Assigned To</label>
              <div>{selectedTask?.employeeName || "Unassigned"}</div>
            </div>
          </div>

          <div className="mt-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">Comments</h6>
              <span className="text-muted small">
                {(selectedTask?.comments || []).length} comment(s)
              </span>
            </div>

            <div className="border rounded p-2 mb-3" style={{ maxHeight: 180, overflowY: "auto" }}>
              {(selectedTask?.comments || []).length === 0 ? (
                <div className="text-muted small">No comments yet</div>
              ) : (
                selectedTask.comments.map((comment, index) => (
                  <div key={comment?._id || index} className="border-bottom pb-2 mb-2">
                    <div className="small">{comment?.comment}</div>
                    <div className="text-muted small">
                      {formatDate(comment?.commentedAt)}
                    </div>
                  </div>
                ))
              )}
            </div>

            <form onSubmit={handleAddComment}>
              <label className="form-label">Add Comment</label>
              <textarea
                className="form-control mb-2"
                rows="3"
                placeholder="Write a comment..."
                value={commentText}
                onChange={(event) => setCommentText(event.target.value)}
              />
              <button
                type="submit"
                className="btn btn-primary btn-sm"
                disabled={savingComment || !commentText.trim()}
              >
                {savingComment ? "Adding..." : "Add Comment"}
              </button>
            </form>
          </div>
        </Modal.Body>
        {!isStaffView && (
          <Modal.Footer>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setSelectedTask(null)}
            >
              Close
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                const currentTask = selectedTask;
                setSelectedTask(null);
                openEditTaskModal(currentTask);
              }}
            >
              Edit Task
            </button>
          </Modal.Footer>
        )}
      </Modal>

      {/* ======================= CREATE TASK MODAL ======================= */}
      <Modal
        show={taskModalOpen}
        onHide={() => setTaskModalOpen(false)}
        centered
        dialogClassName="admin-task-kanban-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Create New Task</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleCreateTask}>
          <Modal.Body>
            <div className="mb-3">
              <label className="form-label">Title *</label>
              <input
                className="form-control"
                name="title"
                value={taskForm.title}
                onChange={handleTaskFormChange}
                placeholder="Task title"
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Description</label>
              <textarea
                className="form-control"
                name="description"
                rows="3"
                value={taskForm.description}
                onChange={handleTaskFormChange}
                placeholder="Task description"
              />
            </div>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Work Order</label>
                <select
                  className="form-select"
                  name="workOrder"
                  value={taskForm.workOrder}
                  onChange={handleTaskFormChange}
                >
                  <option value="">Select Work Order</option>
                  {workOrderOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Assign To</label>
                <select
                  className="form-select"
                  name="assignedEmployeeId"
                  value={taskForm.assignedEmployeeId}
                  onChange={handleTaskFormChange}
                >
                  <option value="">Unassigned</option>
                  {employees.map((employee) => (
                    <option key={getId(employee)} value={getId(employee)}>
                      {employee.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Priority</label>
                <select
                  className="form-select"
                  name="priority"
                  value={taskForm.priority}
                  onChange={handleTaskFormChange}
                >
                  <option>Low</option>
                  <option>Medium</option>
                  <option>High</option>
                  <option>Critical</option>
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Est. Hours</label>
                <input
                  type="number"
                  min="0"
                  className="form-control"
                  name="estimatedHours"
                  value={taskForm.estimatedHours}
                  onChange={handleTaskFormChange}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Due Date</label>
                <input
                  type="date"
                  className="form-control"
                  name="plannedEndDate"
                  value={taskForm.plannedEndDate}
                  onChange={handleTaskFormChange}
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setTaskModalOpen(false)}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={savingTask}>
              {savingTask ? "Creating..." : "Create Task"}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* ======================= EDIT TASK MODAL ======================= */}
      <Modal
        show={editTaskModalOpen}
        onHide={() => setEditTaskModalOpen(false)}
        centered
        dialogClassName="admin-task-kanban-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Task</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleUpdateTask}>
          <Modal.Body>
            <div className="mb-3">
              <label className="form-label">Title *</label>
              <input
                className="form-control"
                name="title"
                value={editTaskForm.title}
                onChange={handleEditTaskFormChange}
                placeholder="Task title"
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Description</label>
              <textarea
                className="form-control"
                name="description"
                rows="3"
                value={editTaskForm.description}
                onChange={handleEditTaskFormChange}
                placeholder="Task description"
              />
            </div>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Work Order</label>
                <select
                  className="form-select"
                  name="workOrder"
                  value={editTaskForm.workOrder}
                  onChange={handleEditTaskFormChange}
                >
                  <option value="">Select Work Order</option>
                  {workOrderOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Assign To</label>
                <select
                  className="form-select"
                  name="assignedEmployeeId"
                  value={editTaskForm.assignedEmployeeId}
                  onChange={handleEditTaskFormChange}
                >
                  <option value="">Unassigned</option>
                  {employees.map((employee) => (
                    <option key={getId(employee)} value={getId(employee)}>
                      {employee.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Priority</label>
                <select
                  className="form-select"
                  name="priority"
                  value={editTaskForm.priority}
                  onChange={handleEditTaskFormChange}
                >
                  <option>Low</option>
                  <option>Medium</option>
                  <option>High</option>
                  <option>Critical</option>
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  name="status"
                  value={editTaskForm.status}
                  onChange={handleEditTaskFormChange}
                >
                  <option value="Unassigned">Unassigned</option>
                  <option value="Assigned">Assigned</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                  <option value="Delayed">Delayed</option>
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Due Date</label>
                <input
                  type="date"
                  className="form-control"
                  name="plannedEndDate"
                  value={editTaskForm.plannedEndDate}
                  onChange={handleEditTaskFormChange}
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setEditTaskModalOpen(false)}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={savingTask}>
              {savingTask ? "Saving..." : "Save Changes"}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* ======================= DELETE CONFIRMATION MODAL ======================= */}
      <Modal
        show={Boolean(deleteTaskCandidate)}
        onHide={() => setDeleteTaskCandidate(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete Task</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete task{" "}
          <strong>"{deleteTaskCandidate?.title}"</strong>? This action cannot be
          undone.
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setDeleteTaskCandidate(null)}
            disabled={savingTask}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleDeleteTask}
            disabled={savingTask}
          >
            {savingTask ? "Deleting..." : "Delete"}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminTaskKanban;
